/**
 * ArticleRoleFilter plugin (articles context).
 *
 * Multi-select filter for ArticleType ("Rola artykułu"). Article-specific —
 * has no equivalent in the calls filter set, hence a dedicated plugin.
 *
 * URL param: articleRole (singular, multiple via getAll)
 * API param: articleRoles (plural)
 * SavedView field: articleRoles
 *
 * Faceted counts come from `facets.articleRoleCounts` (all ArticleType values,
 * zero-filled). Options are derived from articleRoleLabels (single source of
 * truth for the enum domain) so every role renders even with count 0.
 *
 * @see docs/specifications/2026-06-08-articles-filters-unification/spec.md
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../../calls/filters/types";
import { SavedViewFilters } from "../../../../services/grantsApi";
import MultiSelectDropdown from "../../../common/MultiSelectDropdown";
import {
  articleRoleLabels,
} from "../../shared/articleRoleLabels";
import { ArticleRoleCount } from "../../../../services/grantsApi";

/** All ArticleType enum values — derived from labels map (single source of truth). */
const ARTICLE_ROLE_VALUES = Object.keys(articleRoleLabels);

const ArticleRoleFilterComponent = React.memo(
  function ArticleRoleFilterComponent(props: FilterComponentProps<string[]>) {
    const { value, onChange, facets, isLoading } = props;

    const counts = facets?.articleRoleCounts as ArticleRoleCount[] | undefined;

    const options = useMemo(
      () =>
        ARTICLE_ROLE_VALUES.map((role) => ({
          value: role,
          label: articleRoleLabels[role] ?? role,
          count: counts?.find((c) => c.role === role)?.count ?? 0,
        })),
      [counts]
    );

    if (isLoading) {
      return React.createElement("div", {
        className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
      });
    }

    return React.createElement(
      "div",
      { className: "w-64 relative" },
      React.createElement(MultiSelectDropdown, {
        options,
        selectedValues: value,
        onChange,
        placeholder: "Wszystkie role",
        ariaLabel: "Filtruj według roli artykułu",
      })
    );
  }
);

export const articleRoleFilter: FilterPlugin<string[]> = {
  key: "articleRole",
  label: "Rola artykułu",
  placement: "main",
  order: 6,
  defaultValue: [],
  Component: ArticleRoleFilterComponent,

  isActive(value: string[]): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string[], params: URLSearchParams): void {
    value.forEach((r) => params.append("articleRole", r));
  },

  fromUrlParams(params: URLSearchParams): string[] {
    return params
      .getAll("articleRole")
      .filter((r) => ARTICLE_ROLE_VALUES.includes(r));
  },

  toSavedView(value: string[], filters: Partial<SavedViewFilters>): void {
    // articleRoles is an articles-only field; SavedViewFilters is the shared
    // serialization bag. Cast through a loose record (the articles saved-view
    // schema accepts articleRoles).
    // TECH-DEBT: FilterPlugin.toSavedView is typed against the calls-only
    // SavedViewFilters bag, so articles-specific fields require this loose cast.
    // Fixing it would mean generifying FilterPlugin over the saved-view shape
    // (shared API owned by another module) — out of scope for these findings.
    (filters as Record<string, unknown>).articleRoles = value;
  },

  fromSavedView(filters: SavedViewFilters): string[] {
    // TECH-DEBT: see toSavedView — articles-only field read via loose cast
    // because FilterPlugin is typed against the calls-only SavedViewFilters bag.
    const roles = (filters as Record<string, unknown>).articleRoles;
    return Array.isArray(roles)
      ? (roles as string[]).filter((r) => ARTICLE_ROLE_VALUES.includes(r))
      : [];
  },

  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((r) => params.append("articleRoles", r));
  },
};
